import { useEffect, useMemo, useRef, useState } from "react";
import { getUserIdFromToken } from "../utils";
import { RealtimeWsClient } from "../lib/wsClient.js";
import { startAudioCapture } from "../lib/audioCapture.js";
import "./live-translator-styles.css";
import AppBar from '../components/app-bar';

// API URLs - use environment variables in production, fallback to localhost for dev
const getApiUrl = () => {
  // Se há variável de ambiente definida, use-a
  if (process.env.REACT_APP_CAPTIONS_API_URL) {
    return process.env.REACT_APP_CAPTIONS_API_URL;
  }
  // Fallback para desenvolvimento local
  const HTTP_PROTOCOL = window.location.protocol === "https:" ? "https" : "http";
  const HTTP_HOST = window.location.hostname || "localhost";
  return `${HTTP_PROTOCOL}://${HTTP_HOST}:8080`;
};

const getWsUrl = () => {
  // Se há variável de ambiente definida, use-a
  if (process.env.REACT_APP_CAPTIONS_WS_URL) {
    return process.env.REACT_APP_CAPTIONS_WS_URL;
  }
  // Fallback para desenvolvimento local
  const WS_PROTOCOL = window.location.protocol === "https:" ? "wss" : "ws";
  const WS_HOST = window.location.hostname || "localhost";
  return `${WS_PROTOCOL}://${WS_HOST}:8080`;
};

const DEFAULT_HTTP_URL = getApiUrl();
const DEFAULT_WS_URL = getWsUrl();

const ADMIN_LOGIN = "admin";
const ADMIN_PASSWORD = "Password123";
const ADMIN_AUTH_TOKEN = btoa(`${ADMIN_LOGIN}:${ADMIN_PASSWORD}`);

const formatExpiry = (value) => {
  if (!value) return "";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "" : date.toLocaleString();
};

const formatDateOnly = (value) => {
  if (!value) return "";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "" : date.toLocaleDateString("it-IT");
};

const formatTimeOnly = (value) => {
  if (!value) return "";
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? ""
    : date.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" });
};

const formatTimeRange = (start, end) => {
  const startLabel = formatTimeOnly(start);
  const endLabel = formatTimeOnly(end);
  if (startLabel && endLabel) {
    return `${startLabel} - ${endLabel}`;
  }
  return startLabel || endLabel || "";
};

export default function LiveTranslator() {
  console.log("LiveTranslator v2-col loaded - Cache Bust 1");
  const [accessCode, setAccessCode] = useState("");
  const [accessMeetingName, setAccessMeetingName] = useState("");
  const [accessStartAt, setAccessStartAt] = useState("");
  const [accessPassword, setAccessPassword] = useState("");
  const [passwordEnabled, setPasswordEnabled] = useState(true);
  const [accessExpiresAt, setAccessExpiresAt] = useState("");
  const [accessMaxUsers, setAccessMaxUsers] = useState(0);
  const [translationMode, setTranslationMode] = useState("precise");
  const [sourceLanguage, setSourceLanguage] = useState("");
  const [copyStatus, setCopyStatus] = useState("");
  const [error, setError] = useState("");
  const [accessHistory, setAccessHistory] = useState([]);
  const [historySort, setHistorySort] = useState("created");
  const [adminView, setAdminView] = useState("start");
  const [newSessionOpen, setNewSessionOpen] = useState(false);
  const [captureModalOpen, setCaptureModalOpen] = useState(false);
  const [startSearch, setStartSearch] = useState("");
  const [sessionSearch, setSessionSearch] = useState("");
  const [selectedSessionCode, setSelectedSessionCode] = useState("");
  const [selectedSessionTitle, setSelectedSessionTitle] = useState("");
  const [logTab, setLogTab] = useState("original");
  const [editingCode, setEditingCode] = useState("");
  const [inviteText, setInviteText] = useState("");
  const [inviteModalOpen, setInviteModalOpen] = useState(false);
  const [inviteModalText, setInviteModalText] = useState("");
  const [inviteModalStatus, setInviteModalStatus] = useState("");
  const [inviteModalError, setInviteModalError] = useState("");
  const [logModalOpen, setLogModalOpen] = useState(false);
  const [logModalItems, setLogModalItems] = useState([]);
  const [logModalLoading, setLogModalLoading] = useState(false);
  const [logModalError, setLogModalError] = useState("");
  const [logModalCode, setLogModalCode] = useState("");
  const [logModalTitle, setLogModalTitle] = useState("");
  const [status, setStatus] = useState("idle");
  const [statusDetail, setStatusDetail] = useState("Non connesso");
  const [captureError, setCaptureError] = useState("");
  const [targetLanguage, setTargetLanguage] = useState("en");
  const [serverUrl, setServerUrl] = useState(DEFAULT_WS_URL);
  const [includeMic, setIncludeMic] = useState(true);
  const [captions, setCaptions] = useState([]);
  const [finalLines, setFinalLines] = useState([]);
  const [isRunning, setIsRunning] = useState(false);
  const [logItems, setLogItems] = useState([]);
  const [logLoading, setLogLoading] = useState(false);
  const [logError, setLogError] = useState("");

  const wsRef = useRef(null);      // Tab audio WebSocket
  const audioRef = useRef(null);   // Tab audio capture
  const micWsRef = useRef(null);   // Mic audio WebSocket  
  const micAudioRef = useRef(null); // Mic audio capture
  const popoutRef = useRef(null);
  const loggedSeqRef = useRef(new Set());
  const logQueueRef = useRef([]);
  const logFlushTimerRef = useRef(null);
  const pendingLogRef = useRef(new Map());

  const loadHistory = async (sort = historySort) => {
    try {
      const response = await fetch(`${DEFAULT_HTTP_URL}/access/list?sort=${encodeURIComponent(sort)}`);
      const data = await response.json();
      if (data.ok && Array.isArray(data.items)) {
        setAccessHistory(data.items);
      }
    } catch (err) {
      // ignore
    }
  };

  useEffect(() => {
    loadHistory();
  }, []);

  useEffect(() => {
    loadHistory(historySort);
  }, [historySort]);

  const normalizeSearch = (value) => value.trim().toLowerCase();
  const matchesSearch = (item, query) => {
    if (!query) return true;
    const haystack = `${item.meetingName || ""} ${item.code || ""}`.toLowerCase();
    return haystack.includes(query);
  };

  const startSearchValue = normalizeSearch(startSearch);
  const logSearchValue = normalizeSearch(sessionSearch);

  const upcomingItems = useMemo(() => {
    const nowMs = Date.now();
    return accessHistory.filter(
      (item) => (!item.expiresAt || item.expiresAt > nowMs) && matchesSearch(item, startSearchValue)
    );
  }, [accessHistory, startSearchValue]);

  const logListItems = useMemo(
    () => accessHistory.filter((item) => matchesSearch(item, logSearchValue)),
    [accessHistory, logSearchValue]
  );

  const buildInviteMessage = ({ code, password, expiresAt }) => {
    const hasPassword = Boolean(password);
    // Use configured public URL or fallback to current origin (for dev)
    const publicBaseUrl = process.env.REACT_APP_PUBLIC_CAPTIONS_URL || window.location.origin;
    const link = hasPassword
      ? `${publicBaseUrl}/?code=${encodeURIComponent(code)}&password=${encodeURIComponent(password)}`
      : `${publicBaseUrl}/?code=${encodeURIComponent(code)}`;
    const expiresLabel = expiresAt ? formatExpiry(expiresAt) : "not set";
    return `Access Link: ${link}
Code: ${code}
Password: ${password || "none"}
Expires: ${expiresLabel}

Note: The link already includes the password.

Quick Guide (Google Chrome):
1) Open the link in Chrome.
2) Click Start Capture.
3) In the share dialog, select a tab with audio and enable "Share audio".
4) Done! Captions will appear on screen.`;
  };

  const tryCopyToClipboard = async (message) => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(message);
        return true;
      }
    } catch (err) {
      // ignore
    }
    return false;
  };

  const adminHeaders = () => ({
    "x-admin-auth": ADMIN_AUTH_TOKEN
  });

  const fetchLogItems = async (code) => {
    if (!code) return;
    setLogModalLoading(true);
    setLogModalError("");
    setLogModalItems([]);
    try {
      const response = await fetch(`${DEFAULT_HTTP_URL}/captions/list?code=${encodeURIComponent(code)}`, {
        headers: adminHeaders()
      });
      const data = await response.json();
      if (!data.ok) {
        throw new Error(data.error || "Impossibile caricare i log");
      }
      setLogModalItems(data.items || []);
    } catch (err) {
      setLogModalError(err.message || "Impossibile caricare i log");
    } finally {
      setLogModalLoading(false);
    }
  };

  const openLogModal = (item) => {
    setLogModalCode(item.code);
    setLogModalTitle(item.meetingName ? `${item.meetingName} - ${item.code}` : item.code);
    setLogModalOpen(true);
    fetchLogItems(item.code);
  };

  const closeLogModal = () => {
    setLogModalOpen(false);
    setLogModalItems([]);
    setLogModalError("");
    setLogModalCode("");
    setLogModalTitle("");
  };

  const downloadLogDocx = async (code) => {
    if (!code) return;
    setLogModalError("");
    try {
      const response = await fetch(`${DEFAULT_HTTP_URL}/captions/docx?code=${encodeURIComponent(code)}`, {
        headers: adminHeaders()
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || "Impossibile scaricare il DOCX");
      }
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `captions-${code}.docx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      setLogModalError(err.message || "Impossibile scaricare il DOCX");
    }
  };

  const copyInviteFor = async (data) => {
    const message = buildInviteMessage(data);
    setInviteText(message);
    setError("");
    setCopyStatus("");
    const copied = await tryCopyToClipboard(message);
    if (copied) {
      setCopyStatus("Invito copiato. Invialo ai partecipanti.");
      return;
    }
    setError("Copia automatica non riuscita. Usa il testo qui sotto e copia manualmente.");
  };

  const handleStartAtChange = (value) => {
    setAccessStartAt(value);
    if (!value) {
      return;
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return;
    }
    const expires = new Date(date.getTime() + 2 * 60 * 60 * 1000);
    setAccessExpiresAt(expires.toISOString().slice(0, 16));
  };

  const openInviteModal = (data) => {
    const message = buildInviteMessage(data);
    setInviteModalText(message);
    setInviteModalOpen(true);
    setInviteModalStatus("");
    setInviteModalError("");
  };

  const openNewSessionModal = () => {
    resetForm();
    setCopyStatus("");
    setError("");
    setNewSessionOpen(true);
  };

  const closeNewSessionModal = () => {
    setNewSessionOpen(false);
    setCopyStatus("");
    setError("");
  };

  const closeCaptureModal = () => {
    setCaptureModalOpen(false);
    setCaptureError("");
  };

  const closeInviteModal = () => {
    setInviteModalOpen(false);
    setInviteModalText("");
    setInviteModalStatus("");
    setInviteModalError("");
  };

  const copyInviteFromModal = async () => {
    if (!inviteModalText) {
      return;
    }
    setInviteModalStatus("");
    setInviteModalError("");
    const copied = await tryCopyToClipboard(inviteModalText);
    if (copied) {
      setInviteModalStatus("Invito copiato. Invialo ai partecipanti.");
      return;
    }
    setInviteModalError("Copia automatica non riuscita. Seleziona il testo e copia manualmente.");
  };

  const createAccess = async () => {
    setError("");
    setCopyStatus("");

    if (passwordEnabled && !accessPassword) {
      setError("Inserisci una password o disattiva la protezione.");
      return false;
    }

    const payload = {
      meetingName: accessMeetingName,
      password: passwordEnabled ? accessPassword : "",
      startAt: accessStartAt ? new Date(accessStartAt).toISOString() : undefined,
      expiresAt: accessExpiresAt ? new Date(accessExpiresAt).toISOString() : undefined,
      maxUsers: Number(accessMaxUsers) || undefined,
      translationMode: translationMode,
      sourceLanguage: sourceLanguage,
      createdBy: getUserIdFromToken() || ""
    };

    try {
      const response = await fetch(`${DEFAULT_HTTP_URL}/access/create`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload)
      });
      const data = await response.json();
      if (!data.ok) {
        setError(data.error || "Impossibile creare la sessione");
        return false;
      }
      setAccessCode(data.code || accessCode);
      if (data.expiresAt) {
        setAccessExpiresAt(new Date(data.expiresAt).toISOString().slice(0, 16));
      }
      setCopyStatus("Sessione creata con successo");
      loadHistory();
      return true;
    } catch (err) {
      setError(err.message || "Errore durante la creazione");
      return false;
    }
  };

  const resetForm = () => {
    setEditingCode("");
    setAccessCode("");
    setAccessMeetingName("");
    setAccessPassword("");
    setAccessStartAt("");
    setAccessExpiresAt("");
    setAccessMaxUsers(0);
    setPasswordEnabled(true);
    setTranslationMode("fast");
    setSourceLanguage("");
  };

  const updateAccess = async () => {
    setError("");
    setCopyStatus("");

    if (!editingCode) {
      setError("Nessuna sessione selezionata per la modifica.");
      return;
    }

    if (passwordEnabled && !accessPassword) {
      setError("Inserisci una password o disattiva la protezione.");
      return;
    }

    const payload = {
      code: editingCode,
      meetingName: accessMeetingName,
      password: passwordEnabled ? accessPassword : "",
      startAt: accessStartAt ? new Date(accessStartAt).toISOString() : undefined,
      expiresAt: accessExpiresAt ? new Date(accessExpiresAt).toISOString() : undefined,
      maxUsers: Number(accessMaxUsers) || undefined
    };

    try {
      const response = await fetch(`${DEFAULT_HTTP_URL}/access/update`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload)
      });
      const data = await response.json();
      if (!data.ok) {
        setError(data.error || "Impossibile aggiornare la sessione");
        return;
      }
      setCopyStatus("Sessione aggiornata");
      resetForm();
      await loadHistory();
    } catch (err) {
      setError(err.message || "Errore durante l'aggiornamento");
    }
  };

  const deleteAccess = async (code) => {
    setError("");
    setCopyStatus("");
    if (!code) return;
    if (!confirm("Eliminare questa sessione dallo storico?")) {
      return;
    }
    try {
      const response = await fetch(`${DEFAULT_HTTP_URL}/access/delete`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ code })
      });
      const data = await response.json();
      if (!data.ok) {
        setError(data.error || "Impossibile eliminare");
        return;
      }
      if (editingCode && editingCode === code) {
        resetForm();
      }
      await loadHistory();
    } catch (err) {
      setError(err.message || "Errore durante l'eliminazione");
    }
  };

  const copyInvite = async () => {
    if (!accessCode || !accessPassword) {
      setError("Inserisci codice e password prima di copiare l'invito.");
      return;
    }

    await copyInviteFor({ code: accessCode, password: accessPassword, expiresAt: accessExpiresAt });
  };

  const ensurePopout = (ref) => {
    if (ref.current && !ref.current.closed) {
      return ref.current;
    }

    const pop = window.open(
      "",
      "captions",
      "width=520,height=220,menubar=no,toolbar=no,location=no,status=no,resizable=yes"
    );

    if (!pop) {
      return null;
    }

    pop.document.write(`<!doctype html>
<html lang="en">
<head>
<meta charset="UTF-8">
<title>Didascalie in tempo reale</title>
<style>
  @import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Sans:wght@400;600&display=swap');
  body { margin: 0; font-family: 'IBM Plex Sans', system-ui, sans-serif; background: #0d1220; color: #f5f7fb; }
  .toolbar { display: flex; align-items: center; justify-content: flex-end; gap: 6px; padding: 8px 16px 0; }
  .toolbar button { background: rgba(255,255,255,0.1); border: 1px solid rgba(255,255,255,0.2); color: #f5f7fb; border-radius: 6px; cursor: pointer; font-family: inherit; font-weight: 600; padding: 4px 10px; font-size: 14px; transition: background 0.15s; }
  .toolbar button:hover { background: rgba(255,255,255,0.2); }
  .toolbar .size-label { font-size: 11px; color: #98a2b3; min-width: 32px; text-align: center; }
  .wrap { padding: 8px 16px 16px; }
  .label { font-size: 12px; color: #98a2b3; margin-bottom: 6px; }
  .text { font-size: 18px; line-height: 1.5; white-space: pre-line; }
</style>
</head>
<body>
  <div class="toolbar">
    <button id="fontDown" title="Diminuir fonte">A-</button>
    <span class="size-label" id="sizeLabel">18</span>
    <button id="fontUp" title="Aumentar fonte">A+</button>
  </div>
  <div class="wrap">
    <div class="label">Didascalie in tempo reale</div>
    <div class="text" id="caption">In attesa di audio...</div>
  </div>
  <script>
    (function(){
      var size = parseInt(localStorage.getItem('pipFontSize')) || 18;
      var cap = document.getElementById('caption');
      var lbl = document.getElementById('sizeLabel');
      function apply(){ cap.style.fontSize = size+'px'; lbl.textContent = size; localStorage.setItem('pipFontSize', size); }
      apply();
      document.getElementById('fontUp').onclick = function(){ size = Math.min(size+2, 64); apply(); };
      document.getElementById('fontDown').onclick = function(){ size = Math.max(size-2, 10); apply(); };
    })();
  </script>
</body>
</html>`);
    pop.document.close();
    ref.current = pop;
    return pop;
  };

  const openCaptionWindow = async (ref) => {
    if (ref.current && !ref.current.closed) {
      return ref.current;
    }

    if ("documentPictureInPicture" in window) {
      try {
        const pipWindow = await window.documentPictureInPicture.requestWindow({
          width: 520,
          height: 220,
          disallowReturnToOpener: true,
          preferInitialWindowPlacement: true
        });
        pipWindow.document.write(`<!doctype html>
<html lang="en">
<head>
<meta charset="UTF-8">
<title>Didascalie in tempo reale</title>
<style>
  @import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Sans:wght@400;600&display=swap');
  body { margin: 0; font-family: 'IBM Plex Sans', system-ui, sans-serif; background: #0d1220; color: #f5f7fb; }
  .toolbar { display: flex; align-items: center; justify-content: flex-end; gap: 6px; padding: 8px 16px 0; }
  .toolbar button { background: rgba(255,255,255,0.1); border: 1px solid rgba(255,255,255,0.2); color: #f5f7fb; border-radius: 6px; cursor: pointer; font-family: inherit; font-weight: 600; padding: 4px 10px; font-size: 14px; transition: background 0.15s; }
  .toolbar button:hover { background: rgba(255,255,255,0.2); }
  .toolbar .size-label { font-size: 11px; color: #98a2b3; min-width: 32px; text-align: center; }
  .wrap { padding: 8px 16px 16px; }
  .label { font-size: 12px; color: #98a2b3; margin-bottom: 6px; }
  .text { font-size: 18px; line-height: 1.5; white-space: pre-line; }
</style>
</head>
<body>
  <div class="toolbar">
    <button id="fontDown" title="Diminuir fonte">A-</button>
    <span class="size-label" id="sizeLabel">18</span>
    <button id="fontUp" title="Aumentar fonte">A+</button>
  </div>
  <div class="wrap">
    <div class="label">Didascalie in tempo reale</div>
    <div class="text" id="caption">In attesa di audio...</div>
  </div>
  <script>
    (function(){
      var size = parseInt(localStorage.getItem('pipFontSize')) || 18;
      var cap = document.getElementById('caption');
      var lbl = document.getElementById('sizeLabel');
      function apply(){ cap.style.fontSize = size+'px'; lbl.textContent = size; localStorage.setItem('pipFontSize', size); }
      apply();
      document.getElementById('fontUp').onclick = function(){ size = Math.min(size+2, 64); apply(); };
      document.getElementById('fontDown').onclick = function(){ size = Math.max(size-2, 10); apply(); };
    })();
  </script>
</body>
</html>`);
        pipWindow.document.close();
        ref.current = pipWindow;
        return pipWindow;
      } catch (error) {
        return ensurePopout(ref);
      }
    }

    return ensurePopout(ref);
  };

  const updatePopout = (ref, caption) => {
    const pop = ref.current;
    if (!pop || pop.closed) {
      return;
    }

    const node = pop.document.getElementById("caption");
    if (node) {
      node.textContent = caption || "...";
    }
  };

  const buildWsUrl = (baseUrl, code, password) => {
    try {
      const url = new URL(baseUrl);
      if (code) {
        url.searchParams.set("code", code);
      }
      if (password) {
        url.searchParams.set("password", password);
      }
      return url.toString();
    } catch (err) {
      return baseUrl;
    }
  };

  const handleStatus = ({ state }) => {
    setStatus(state);
    setStatusDetail(state === "connected" ? "Connesso" : state === "connecting" ? "Connessione..." : "Disconnesso");
  };

  const enqueueLogEntry = (entry) => {
    if (!accessCode) {
      return;
    }
    logQueueRef.current.push(entry);
    if (!logFlushTimerRef.current) {
      logFlushTimerRef.current = setTimeout(() => {
        flushLogs();
      }, 1000);
    }
  };

  const flushLogs = async () => {
    if (!accessCode) {
      logQueueRef.current = [];
      logFlushTimerRef.current = null;
      return;
    }
    const entries = logQueueRef.current.splice(0);
    logFlushTimerRef.current = null;
    if (!entries.length) {
      return;
    }
    try {
      await fetch(`${DEFAULT_HTTP_URL}/captions/log`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...adminHeaders() },
        body: JSON.stringify({ code: accessCode, entries })
      });
    } catch (err) {
      // ignore logging errors
    }
  };

  // source: 'tab' | 'mic' - determines where captions are displayed
  const handleCaption = (caption, source = 'tab') => {
    // Add source to caption for tracking
    const captionWithSource = { ...caption, source };

    setCaptions((prev) => {
      const index = prev.findIndex((item) => item.seq === caption.seq && item.source === source);
      let next;
      if (index >= 0) {
        next = [...prev];
        next[index] = { ...next[index], ...captionWithSource };
      } else {
        next = [captionWithSource, ...prev];
      }
      next.sort((a, b) => (b.seq ?? 0) - (a.seq ?? 0));
      return next;
    });

    // PiP: Only show TAB captions (not mic)
    if (source === 'tab') {
      const displayText = caption.translatedText || "";
      if (caption.isFinal && displayText) {
        setFinalLines((prev) => {
          const next = [...prev, displayText].slice(-3);
          updatePopout(popoutRef, next.join("\n"));
          return next;
        });
      } else if (!caption.isFinal && displayText) {
        const preview = finalLines.length ? [...finalLines, displayText].slice(-3) : [displayText];
        updatePopout(popoutRef, preview.join("\n"));
      }
    }

    // Logs: Only log captions that have a translatedText (skip source-only entries)
    if (caption.isFinal && caption.translatedText) {
      const key = `${source}-${caption.seq ?? `${caption.receivedAt}-${caption.sourceText}`}`;
      const entry = {
        receivedAt: caption.receivedAt || Date.now(),
        speaker: source === 'mic' ? '🎤 You' : (caption.speaker ?? ""),
        sourceText: caption.sourceText || "",
        translatedText: caption.translatedText || "",
        language: caption.language || "",
        source: source
      };

      if (!loggedSeqRef.current.has(key)) {
        loggedSeqRef.current.add(key);
        enqueueLogEntry(entry);
      }
    }
  };

  const startCapture = async (override = {}) => {
    setCaptureError("");

    const code = override.code ?? accessCode;
    const password = override.password ?? accessPassword;

    if (!code) {
      setCaptureError("Inserisci il codice di accesso prima di avviare la cattura.");
      return;
    }

    if (override.code && override.code !== accessCode) {
      setAccessCode(override.code);
    }
    if (override.password !== undefined && override.password !== accessPassword) {
      setAccessPassword(override.password);
    }

    await openCaptionWindow(popoutRef);

    const wsUrl = buildWsUrl(serverUrl, code, password);

    // TAB WebSocket - captions go to PiP + Logs
    const tabWsClient = new RealtimeWsClient({
      url: wsUrl,
      onCaption: (caption) => handleCaption(caption, 'tab'),
      onStatus: handleStatus,
      onError: (payload) => {
        const detail = payload.detail ? ` (${payload.detail})` : "";
        const msg = payload.message || "Errore sconosciuto";
        setCaptureError(`Tab: ${msg}${detail}`);
      }
    });

    tabWsClient.connect({
      language: targetLanguage
    });

    wsRef.current = tabWsClient;

    try {
      console.log('[LiveTranslator] Starting TAB capture');

      // TAB audio capture (display only)
      const tabAudio = await startAudioCapture({
        targetSampleRate: 16000,
        chunkSeconds: 0.3,
        captureSource: "display",
        onChunk: (chunk) => {
          tabWsClient.sendAudio(chunk.buffer);
        },
        onStatus: (msg) => setStatusDetail(`Tab: ${msg}`)
      });
      audioRef.current = tabAudio;

      // MIC capture if enabled - separate connection
      if (includeMic) {
        console.log('[LiveTranslator] Starting MIC capture');

        const micWsClient = new RealtimeWsClient({
          url: wsUrl,
          onCaption: (caption) => handleCaption(caption, 'mic'),
          onStatus: () => { }, // Silent for mic
          onError: (payload) => {
            console.warn('[MIC] WebSocket error:', payload);
          }
        });

        micWsClient.connect({
          language: targetLanguage
        });

        micWsRef.current = micWsClient;

        try {
          const micAudio = await startAudioCapture({
            targetSampleRate: 16000,
            chunkSeconds: 0.3,
            captureSource: "mic",
            onChunk: (chunk) => {
              micWsClient.sendAudio(chunk.buffer);
            },
            onStatus: (msg) => console.log('[MIC]', msg)
          });
          micAudioRef.current = micAudio;
          setStatusDetail('🎙️ Capture active: Tab + Mic');
        } catch (micErr) {
          console.warn('[MIC] Audio capture failed:', micErr.message);
          setStatusDetail('🎙️ Capture active: Tab only (mic unavailable)');
        }
      }

      setIsRunning(true);
      fetchLogList({ silent: true, codeOverride: code });
    } catch (err) {
      setCaptureError(err.message || "Cattura audio non riuscita");
      tabWsClient.close();
      setIsRunning(false);
    }
  };

  const stopCapture = async () => {
    setIsRunning(false);

    // Stop Tab connections
    wsRef.current?.close();
    wsRef.current = null;
    if (audioRef.current) {
      await audioRef.current.stop();
      audioRef.current = null;
    }

    // Stop Mic connections
    micWsRef.current?.close();
    micWsRef.current = null;
    if (micAudioRef.current) {
      await micAudioRef.current.stop();
      micAudioRef.current = null;
    }

    // Close PiP window
    if (popoutRef.current && !popoutRef.current.closed) {
      popoutRef.current.close();
      popoutRef.current = null;
    }

    await flushLogs();
    setStatus("idle");
    setStatusDetail("Stopped");
  };

  const fetchLogList = async ({ silent = false, codeOverride } = {}) => {
    const code = codeOverride ?? accessCode;
    if (!code) {
      setLogItems([]);
      return;
    }
    if (!silent) {
      setLogLoading(true);
      setLogError("");
    }
    try {
      const response = await fetch(`${DEFAULT_HTTP_URL}/captions/list?code=${encodeURIComponent(code)}`, {
        headers: adminHeaders()
      });
      const data = await response.json();
      if (!data.ok) {
        throw new Error(data.error || "Impossibile caricare i log");
      }
      setLogItems(data.items || []);
    } catch (err) {
      if (!silent) {
        setLogError(err.message || "Impossibile caricare i log");
      }
    } finally {
      if (!silent) {
        setLogLoading(false);
      }
    }
  };

  const applySessionToForm = (item) => {
    if (!item) return;
    setEditingCode("");
    setAccessCode(item.code || "");
    setAccessMeetingName(item.meetingName || "");
    setAccessPassword(item.password || "");
    setAccessStartAt(item.startAt ? new Date(item.startAt).toISOString().slice(0, 16) : "");
    setAccessExpiresAt(item.expiresAt ? new Date(item.expiresAt).toISOString().slice(0, 16) : "");
    setAccessMaxUsers(item.maxUsers || 0);
  };

  const handleEditSession = (item) => {
    if (!item) return;
    setEditingCode(item.code);
    setAccessCode(item.code);
    setAccessMeetingName(item.meetingName || "");
    setAccessPassword(item.password || "");
    setAccessStartAt(item.startAt ? new Date(item.startAt).toISOString().slice(0, 16) : "");
    setAccessExpiresAt(item.expiresAt ? new Date(item.expiresAt).toISOString().slice(0, 16) : "");
    setAccessMaxUsers(item.maxUsers || 0);
    setPasswordEnabled(Boolean(item.password));
    setNewSessionOpen(true);
    setError("");
    setCopyStatus("");
  };

  const handleStartSession = async (item) => {
    if (!item) return;
    applySessionToForm(item);
    setCaptureModalOpen(true);
  };

  const selectSession = (item) => {
    if (!item) return;
    setSelectedSessionCode(item.code);
    setSelectedSessionTitle(item.meetingName ? `${item.meetingName}` : item.code);
  };


  useEffect(() => {
    return () => {
      wsRef.current?.close();
      audioRef.current?.stop();
      if (popoutRef.current && !popoutRef.current.closed) {
        popoutRef.current.close();
      }
      flushLogs();
      pendingLogRef.current.forEach((timeoutId) => clearTimeout(timeoutId));
      pendingLogRef.current.clear();
    };
  }, []);

  useEffect(() => {
    loggedSeqRef.current.clear();
    logQueueRef.current = [];
    pendingLogRef.current.forEach((timeoutId) => clearTimeout(timeoutId));
    pendingLogRef.current.clear();
  }, [accessCode]);

  useEffect(() => {
    if (!accessCode) {
      return undefined;
    }
    if (!isRunning) {
      fetchLogList({ silent: true });
      return undefined;
    }
    const intervalId = setInterval(() => {
      fetchLogList({ silent: true });
    }, 3000);
    return () => clearInterval(intervalId);
  }, [isRunning, accessCode]);

  useEffect(() => {
    if (adminView !== "logs") {
      return;
    }
    const hasSelected = logListItems.some((item) => item.code === selectedSessionCode);
    if (!logListItems.length) {
      if (selectedSessionCode) {
        setSelectedSessionCode("");
        setSelectedSessionTitle("");
        setLogModalItems([]);
      }
      setLogModalLoading(false);
      setLogModalError("");
      return;
    }
    if (!selectedSessionCode || !hasSelected) {
      if (logListItems.length) {
        selectSession(logListItems[0]);
      }
      return;
    }
    fetchLogItems(selectedSessionCode);
  }, [adminView, selectedSessionCode, logListItems]);

  return (
    <>
      <AppBar>
        <div className="app admin-page" style={{ paddingTop: '100px', maxWidth: '1700px', minWidth: '1100px', margin: '0 auto' }}>

          <div className="admin-topbar" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '32px' }}>
            <div className="segmented">
              <button
                type="button"
                className={`segment ${adminView === "start" ? "active" : ""}`}
                onClick={() => setAdminView("start")}
              >
                Avvia una sessione
              </button>
              <button
                type="button"
                className={`segment ${adminView === "logs" ? "active" : ""}`}
                onClick={() => setAdminView("logs")}
              >
                Vedi sessioni
              </button>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '24px' }}>
              <button
                type="button"
                className="ghost new-session-btn"
                onClick={openNewSessionModal}
                style={{ fontWeight: 600, color: '#991b1b', border: 'none', background: 'transparent', cursor: 'pointer', padding: 0 }}
              >
                <span className="icon" style={{ marginRight: '6px', fontSize: '1.1rem' }}>+</span>
                Nuova sessione
              </button>
            </div>
          </div>

          {adminView === "start" ? (
            <main className="admin-shell admin-start">
              <section className="card admin-panel session-panel">
                {/* Header removed */}
                <div className="session-list" style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  {upcomingItems.length === 0 && (
                    <div className="helper">Nessuna sessione futura disponibile.</div>
                  )}
                  {upcomingItems.map((item) => {
                    const dateLabel = formatDateOnly(item.startAt || item.expiresAt);
                    const timeLabel = formatTimeRange(item.startAt, item.expiresAt);
                    return (
                      <div key={item.code} className="session-card">
                        <div className="session-main" style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                          <h3 className="session-title" style={{ fontSize: '1rem', fontWeight: 600, color: '#111827', margin: 0 }}>
                            {item.meetingName || "Sessione senza nome"}
                          </h3>
                          <div className="session-meta" style={{ display: 'flex', alignItems: 'center', gap: '16px', fontSize: '0.875rem', color: '#6b7280' }}>
                            {dateLabel && (
                              <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M8 2v4"></path><path d="M16 2v4"></path><rect width="18" height="18" x="3" y="4" rx="2"></rect><path d="M3 10h18"></path></svg>
                                {dateLabel}
                              </span>
                            )}
                            {timeLabel && (
                              <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 6v6l4 2"></path><circle cx="12" cy="12" r="10"></circle></svg>
                                {timeLabel}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="session-actions" style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <button type="button" className="ghost icon-only" title="Copia invito" onClick={() => copyInviteFor(item)} style={{ border: '1px solid #e5e7eb', borderRadius: '8px', padding: '8px', color: '#6b7280', background: '#fff' }}>
                              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"></path><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"></path></svg>
                            </button>
                            <button type="button" className="ghost icon-only" title="Modifica sessione" onClick={() => handleEditSession(item)} style={{ border: '1px solid #e5e7eb', borderRadius: '8px', padding: '8px', color: '#6b7280', background: '#fff' }}>
                              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z"></path></svg>
                            </button>
                            <button type="button" className="ghost icon-only danger" title="Elimina sessione" onClick={() => deleteAccess(item.code)} style={{ border: '1px solid #e5e7eb', borderRadius: '8px', padding: '8px', color: '#6b7280', background: '#fff' }}>
                              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18"></path><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"></path><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"></path></svg>
                            </button>
                          </div>
                          <button type="button" className="primary btn-play" onClick={() => handleStartSession(item)} style={{ background: '#991b1b', color: '#fff', border: 'none', borderRadius: '8px', padding: '8px 16px', display: 'flex', alignItems: 'center', gap: '8px', fontWeight: 600, cursor: 'pointer' }}>
                            <span style={{ fontSize: '0.875rem' }}>Avvia sessione</span>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                              <path d="M5 3l14 9-14 9V3z" />
                            </svg>
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </section>
            </main>
          ) : (
            <main className="admin-shell admin-logs">
              <section className="card admin-panel session-panel">
                <div className="card-header">
                  <h2>Storico sessioni</h2>
                </div>
                <div className="panel-toolbar">
                  <div className="search-field">
                    <input
                      value={sessionSearch}
                      onChange={(event) => setSessionSearch(event.target.value)}
                      placeholder="Cerca sessione..."
                    />
                  </div>
                </div>
                <div className="session-list" style={{ flex: 1, minHeight: 0, overflowY: 'auto', paddingRight: '4px' }}>
                  {logListItems.length === 0 && <div className="helper">Nessuna sessione trovata.</div>}
                  {logListItems.map((item) => {
                    const dateLabel = formatDateOnly(item.startAt || item.expiresAt);
                    const timeLabel = formatTimeRange(item.startAt, item.expiresAt);
                    const isActive = selectedSessionCode === item.code;
                    return (
                      <div
                        key={item.code}
                        className={`session-list-item ${isActive ? "active" : ""}`}
                        onClick={() => selectSession(item)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(event) => {
                          if (event.key === "Enter" || event.key === " ") selectSession(item);
                        }}
                        style={{
                          padding: '16px', borderRadius: '12px', cursor: 'pointer', transition: 'all 0.2s', marginBottom: '12px',
                          border: isActive ? '1px solid #991b1b' : '1px solid transparent',
                          backgroundColor: isActive ? '#fef2f2' : 'transparent',
                        }}
                        onMouseEnter={(e) => { if (!isActive) { e.currentTarget.style.backgroundColor = '#f9fafb'; e.currentTarget.style.borderColor = '#e5e7eb'; } }}
                        onMouseLeave={(e) => { if (!isActive) { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.borderColor = 'transparent'; } }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                          <h4 style={{ fontWeight: 700, margin: 0, fontSize: '0.95rem', color: isActive ? '#7f1d1d' : '#1f2937' }}>
                            {item.meetingName || "Sessione senza nome"}
                          </h4>
                          <button
                            type="button"
                            className="ghost icon-only danger"
                            style={{
                              padding: '6px', borderRadius: '50%', background: isActive ? '#fff' : '#f3f4f6', color: isActive ? '#b91c1c' : '#9ca3af', border: 'none', cursor: 'pointer'
                            }}
                            title="Elimina sessione"
                            onClick={(event) => {
                              event.stopPropagation();
                              deleteAccess(item.code);
                            }}
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18"></path><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"></path><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"></path></svg>
                          </button>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', fontSize: '0.75rem', color: '#6b7280' }}>
                          {dateLabel && (
                            <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                              <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M8 2v4"></path><path d="M16 2v4"></path><rect width="18" height="18" x="3" y="4" rx="2"></rect><path d="M3 10h18"></path></svg>
                              {dateLabel}
                            </span>
                          )}
                          {timeLabel && (
                            <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                              <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 6v6l4 2"></path><circle cx="12" cy="12" r="10"></circle></svg>
                              {timeLabel}
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </section>

              <section className="card admin-panel logs-panel" style={{ flex: '2', display: 'flex', flexDirection: 'column', overflow: 'hidden', padding: 0 }}>
                {/* Header */}
                <div style={{ padding: '24px', borderBottom: '1px solid #f3f4f6', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <h2 style={{ fontSize: '1.25rem', fontWeight: 700, margin: 0, color: '#111827' }}>
                      {selectedSessionTitle || "Seleziona una sessione"}
                    </h2>
                    <p style={{ margin: 0, fontSize: '0.875rem', color: '#6b7280', marginTop: '4px' }}>
                      {selectedSessionCode ? `Trascrizione della sessione ${selectedSessionCode}` : "Scegli una sessione per vedere i log."}
                    </p>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div style={{ background: '#f3f4f6', padding: '4px', borderRadius: '8px', display: 'inline-flex' }}>
                      <button
                        type="button"
                        style={{
                          padding: '6px 16px', borderRadius: '6px', fontSize: '0.875rem', fontWeight: 500,
                          background: logTab === "original" ? '#fff' : 'transparent',
                          color: logTab === "original" ? '#111827' : '#6b7280',
                          boxShadow: logTab === "original" ? '0 1px 2px 0 rgba(0, 0, 0, 0.05)' : 'none',
                          border: 'none', cursor: 'pointer', transition: 'all 0.2s'
                        }}
                        onClick={() => setLogTab("original")}
                      >
                        Originale
                      </button>
                      <button
                        type="button"
                        style={{
                          padding: '6px 16px', borderRadius: '6px', fontSize: '0.875rem', fontWeight: 500,
                          background: logTab === "translated" ? '#fff' : 'transparent',
                          color: logTab === "translated" ? '#111827' : '#6b7280',
                          boxShadow: logTab === "translated" ? '0 1px 2px 0 rgba(0, 0, 0, 0.05)' : 'none',
                          border: 'none', cursor: 'pointer', transition: 'all 0.2s'
                        }}
                        onClick={() => setLogTab("translated")}
                      >
                        Traduzione
                      </button>
                    </div>
                    <button
                      type="button"
                      disabled={!selectedSessionCode}
                      onClick={() => downloadLogDocx(selectedSessionCode)}
                      style={{
                        display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 16px',
                        border: '1px solid #e5e7eb', borderRadius: '8px', fontSize: '0.875rem', fontWeight: 600,
                        color: '#374151', background: '#fff', cursor: 'pointer'
                      }}
                    >
                      Esporta Doc
                    </button>
                  </div>
                </div>

                {logModalLoading && <div className="helper" style={{ padding: '24px' }}>Caricamento...</div>}
                {logModalError && <div className="helper error" style={{ padding: '24px', color: '#b91c1c' }}>{logModalError}</div>}
                {!logModalLoading && !logModalError && logModalItems.length === 0 && (
                  <div className="helper" style={{ padding: '24px' }}>Nessun log disponibile.</div>
                )}

                <div className="log-list" style={{ flex: 1, padding: '24px', display: 'flex', flexDirection: 'column', gap: '24px', backgroundColor: '#f9fafb', overflowY: 'auto' }}>
                  {logModalItems.map((item) => {
                    const text = logTab === "original" ? item.sourceText : item.translatedText || "";
                    const speakerId = parseInt(item.speaker || "0", 10);
                    const colors = ["#0ea5e9", "#22c55e", "#eab308", "#f97316", "#ec4899", "#8b5cf6", "#14b8a6", "#f43f5e"];
                    const color = colors[speakerId % colors.length] || colors[0];

                    return (
                      <div key={item.id} className="log-item" style={{ display: 'flex', flexDirection: 'column', maxWidth: '85%', borderLeft: item.speaker ? `4px solid ${color}` : "4px solid transparent" }}>
                        <div style={{ marginBottom: '4px', marginLeft: '8px', display: 'flex', alignItems: 'center' }}>
                          <span style={{ fontWeight: 700, fontSize: '0.875rem', color: color }}>
                            {item.speaker ? (item.speaker === "You" ? "Tu" : `Speaker ${item.speaker}`) : "Speaker 1"}
                          </span>
                          <span style={{ fontSize: '0.75rem', color: '#9ca3af', marginLeft: '8px' }}>
                            {item.receivedAt ? new Date(item.receivedAt).toLocaleTimeString("it-IT", { hour: '2-digit', minute: '2-digit' }) : "-"}
                          </span>
                        </div>
                        <div style={{
                          padding: '16px', borderRadius: '16px', borderTopLeftRadius: 0,
                          backgroundColor: '#fff', border: '1px solid #f3f4f6',
                          boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
                          color: '#374151', fontSize: '0.95rem', lineHeight: 1.5
                        }}>
                          {text || "(Vuoto)"}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </section>
            </main>
          )}

          {newSessionOpen && (
            <div className="modal-backdrop" onClick={closeNewSessionModal}>
              <div
                className="modal"
                role="dialog"
                aria-modal="true"
                aria-labelledby="new-session-title"
                onClick={(event) => event.stopPropagation()}
              >
                <div className="modal-header">
                  <h3 id="new-session-title" className="modal-title">Controllo accessi</h3>
                  <p className="modal-subtitle">Imposta codice, password e scadenza per gli ospiti.</p>
                </div>
                <div className="form-grid form-grid-2col">
                  {/* Row 1: Codice generato | Nome riunione */}
                  <div className="row-2-col">
                    <div className="inline-field">
                      <label>
                        <span className="label-text">Codice generato</span>
                        <input value={accessCode} readOnly placeholder="Auto-generato" className="input-dark" />
                      </label>
                      <button
                        type="button"
                        className="ghost small"
                        onClick={async () => {
                          if (!accessCode) return;
                          const copied = await tryCopyToClipboard(accessCode);
                          if (copied) {
                            setCopyStatus("Codice copiato.");
                          } else {
                            setError("Impossibile copiare il codice.");
                          }
                        }}
                      >
                        Copia
                      </button>
                    </div>
                    <label>
                      <span className="label-text">Nome riunione</span>
                      <input
                        value={accessMeetingName}
                        onChange={(event) => setAccessMeetingName(event.target.value)}
                        placeholder="Es. Revisione Finanziaria Q2"
                        className="input-dark"
                      />
                    </label>
                  </div>

                  {/* Row 2: Inizio riunione | Scadenza link */}
                  <div className="row-2-col">
                    <label>
                      <span className="label-text">Inizio riunione (data/ora)</span>
                      <input
                        type="datetime-local"
                        value={accessStartAt}
                        onChange={(event) => handleStartAtChange(event.target.value)}
                        className="input-dark"
                      />
                    </label>
                    <label>
                      <span className="label-text">Scadenza link (data/ora)</span>
                      <input
                        type="datetime-local"
                        value={accessExpiresAt}
                        onChange={(event) => setAccessExpiresAt(event.target.value)}
                        className="input-dark"
                      />
                    </label>
                  </div>

                  {/* Row 3: Abilita password | Password */}
                  <div className="row-2-col">
                    <label>
                      <span className="label-text">Abilita password</span>
                      <select
                        value={passwordEnabled ? "yes" : "no"}
                        onChange={(event) => setPasswordEnabled(event.target.value === "yes")}
                        className="input-white"
                      >
                        <option value="yes">Sì</option>
                        <option value="no">No</option>
                      </select>
                    </label>
                    <label>
                      <span className="label-text">Password</span>
                      <input
                        type="password"
                        value={accessPassword}
                        onChange={(event) => setAccessPassword(event.target.value)}
                        placeholder="Imposta password sicura"
                        disabled={!passwordEnabled}
                        className="input-dark"
                      />
                    </label>
                  </div>

                  {/* Row 4: Modalità traduzione | Limite utenti */}
                  <div className="row-2-col">
                    <label>
                      <span className="label-text">Modalità traduzione</span>
                      <input
                        type="text"
                        value="Modello standard"
                        readOnly
                        className="input-dark"
                        style={{ opacity: 0.7, cursor: 'not-allowed' }}
                      />
                    </label>
                    <label>
                      <span className="label-text">Limite utenti (0 = illimitati)</span>
                      <input
                        type="number"
                        min="0"
                        value={accessMaxUsers}
                        onChange={(event) => setAccessMaxUsers(event.target.value)}
                        className="input-dark"
                      />
                    </label>
                  </div>

                  {/* Row 5: Lingua della riunione */}
                  <div className="row-2-col">
                    <label>
                      <span className="label-text">Lingua della riunione</span>
                      <select
                        value={sourceLanguage}
                        onChange={(event) => setSourceLanguage(event.target.value)}
                        className="input-white"
                      >
                        <option value="">Rilevamento automatico</option>
                        <option value="it">Italiano</option>
                        <option value="en">Inglese</option>
                        <option value="pt">Portoghese</option>
                        <option value="es">Spagnolo</option>
                        <option value="de">Tedesco</option>
                        <option value="fr">Francese</option>
                        <option value="el">Greco</option>
                      </select>
                    </label>
                    <div></div>
                  </div>
                </div>
                <div className="modal-actions
 right-aligned">
                  <button type="button" className="ghost" onClick={closeNewSessionModal}>
                    Annulla
                  </button>
                  <button
                    type="button"
                    className="primary admin-red"
                    onClick={async () => {
                      if (editingCode) {
                        await updateAccess();
                        // Assuming success, close modal. Real implementation would check status.
                        closeNewSessionModal();
                      } else {
                        const ok = await createAccess();
                        if (ok) closeNewSessionModal();
                      }
                    }}
                  >
                    {editingCode ? "Salva modifiche" : "Crea accesso"}
                  </button>
                </div>
                {copyStatus && <div className="helper success">{copyStatus}</div>}
                {error && <div className="helper error">{error}</div>}
              </div>
            </div>
          )}

        </div>
      </AppBar>

      {captureModalOpen && (
        <div className="capture-fullscreen">
          <header className="capture-header">
            <a onClick={closeCaptureModal} className="back-link">
              ← Back to Organizer
            </a>
          </header>

          <main className="capture-main">
            <div className="capture-column">

              {/* Access Card */}
              <div className="capture-card-item">
                <div className="capture-card-title">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                    <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                  </svg>
                  Access
                </div>
                <p className="helper" style={{ marginTop: 0, marginBottom: 16 }}>Enter the code and, if provided, the password from the organizer.</p>

                <div style={{ marginBottom: 16 }}>
                  <span className="capture-label">ACCESS CODE</span>
                  <input value={accessCode} readOnly className="capture-input" />
                </div>

                <div>
                  <span className="capture-label">PASSWORD</span>
                  <input type="password" value={accessPassword || "Provided password"} readOnly className="capture-input" style={!accessPassword ? { color: '#9ca3af', fontStyle: 'italic' } : {}} />
                </div>
              </div>

              {/* Connection Card */}
              <div className="capture-card-item">
                <div className="capture-card-title">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M5 12.55a11 11 0 0 1 14.08 0"></path>
                    <path d="M1.42 9a16 16 0 0 1 21.16 0"></path>
                    <path d="M8.53 16.11a6 6 0 0 1 6.95 0"></path>
                    <line x1="12" y1="20" x2="12.01" y2="20"></line>
                  </svg>
                  Connection
                </div>
                <div>
                  <span className="capture-label">TRANSLATION LANGUAGE</span>
                  <select
                    value={targetLanguage}
                    onChange={(event) => {
                      setTargetLanguage(event.target.value);
                      wsRef.current?.updateConfig({ language: event.target.value });
                    }}
                    className="capture-input"
                  >
                    <option value="en">English</option>
                    <option value="es">Spanish</option>
                    <option value="fr">French</option>
                    <option value="de">German</option>
                    <option value="it">Italian</option>
                    <option value="pt-BR">Portuguese (Brazil)</option>
                    <option value="pt-PT">Portuguese (Portugal)</option>
                    <option value="ja">Japanese</option>
                    <option value="zh-CN">Chinese (Simplified)</option>
                  </select>
                </div>

                <div style={{ marginTop: 16, display: 'flex', alignItems: 'center', gap: 10 }}>
                  <input
                    type="checkbox"
                    id="mic-toggle"
                    checked={includeMic}
                    onChange={(e) => setIncludeMic(e.target.checked)}
                    style={{ width: 18, height: 18, cursor: 'pointer', accentColor: 'var(--primary, #9b1616)' }}
                  />
                  <label htmlFor="mic-toggle" className="capture-label" style={{ marginBottom: 0, cursor: 'pointer' }}>
                    ENABLE MICROPHONE
                  </label>
                </div>
              </div>

              {/* Capture Card */}
              <div className="capture-card-item">
                <div className="capture-card-title">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polygon points="5 3 19 12 5 21 5 3"></polygon>
                  </svg>
                  Capture
                </div>
                <p className="helper" style={{ marginTop: 0, marginBottom: 20 }}>Start or stop capture and open the captions window.</p>

                <button
                  type="button"
                  className="primary admin-red btn-full"
                  onClick={isRunning ? stopCapture : startCapture}
                >
                  {isRunning ? (
                    <>
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="4" width="4" height="16"></rect><rect x="14" y="4" width="4" height="16"></rect></svg>
                      Stop capture
                    </>
                  ) : (
                    <>
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><polygon points="5 3 19 12 5 21 5 3"></polygon></svg>
                      Start capture
                    </>
                  )}
                </button>

                {captureError && <div className="helper error" style={{ marginTop: 12 }}>{captureError}</div>}
                {isRunning && <div className="helper success" style={{ marginTop: 12 }}>● {statusDetail || "Capture active"}</div>}
              </div>
            </div>

            <div className="capture-column">
              <div className="info-panel">
                <div className="info-title">
                  <div className="info-icon-wrapper">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="10"></circle>
                      <path d="M12 16v-4"></path>
                      <path d="M12 8h.01"></path>
                    </svg>
                  </div>
                  How it works (Chrome)
                </div>

                <div className="info-steps">
                  <div className="info-step">
                    <div className="step-number">1</div>
                    <div className="step-content">
                      <h4>Enter Credentials</h4>
                      <p>Input the Access Code and Password provided by your session organizer in the fields on the left.</p>
                    </div>
                  </div>

                  <div className="info-step">
                    <div className="step-number">2</div>
                    <div className="step-content">
                      <h4>Start Capture</h4>
                      <p>Click the 'Start capture' button. This will initialize the translation engine.</p>
                    </div>
                  </div>

                  <div className="info-step">
                    <div className="step-number">3</div>
                    <div className="step-content">
                      <h4>Select & Share</h4>
                      <p>A browser dialog will appear. Select the <strong>Tab</strong> that is playing the meeting audio and ensure <span style={{ color: '#dc2626', fontWeight: 700 }}>"Share audio"</span> is checked.</p>
                    </div>
                  </div>

                  <div className="info-step">
                    <div className="step-number">4</div>
                    <div className="step-content">
                      <h4>Live Captions</h4>
                      <p>The real-time translation will automatically appear on your screen overlay immediately after sharing.</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </main>
        </div>
      )}
    </>
  );
}
